// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React from 'react';
import { Button, Grid, Paper, withStyles } from '@material-ui/core';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { State } from '../../redux/root-reducer';
import { CloseModal, closeModalAction } from '../actions';
import { HandleSubmit } from '../../workflow-navigation';
import styles, { StyledComponent } from '../styles';

import { viewModalAction } from '../../confirmation-dialog';
import { getClaim, Claim } from '../../claim';
import { ClaimType } from '../../claim/claim-types';

import { ClaimantRequirements } from '../../assessment/synopsis/common/requirements-management/reducer';
import {
    DeathRequirements,
    getDocumentsRequirementsFromClaim,
} from '../../assessment/synopsis/death/manage-requirements/reducer';
import { getClaimantOptions } from '../../assessment/synopsis/death/manage-requirements/communications/communications-edit-modal';

import { CommunicationsPreviewEmail } from '../../assessment/synopsis/death/manage-requirements/communications/communications-preview-modal';
import { MissingInformationContent } from '../../assessment/synopsis/death/manage-requirements/communications/missing-infomarion-content';
import { communicationAction } from '../../assessment/synopsis/death/manage-requirements/actions/communication-action';

type DispatchProps = {
    closeModal: CloseModal;
    openViewModal: any;
    sendCommunication: (payload: any) => Promise<any>;
};

type Props = StyledComponent &
    DispatchProps & {
        readonly formView: React.ComponentType<any>;
        readonly onSubmit?: HandleSubmit;
        readonly closeButton?: boolean;
        readonly formStyle?: any;
        readonly isButtonsHidden?: boolean;
        readonly modal?: any;
        readonly claim: Claim;
        readonly claimantRequirements: ClaimantRequirements[];
        readonly lifeAssuredRequirements?: DeathRequirements;
        readonly previewScreen: any;
        readonly showPreviewButton?: boolean;
        readonly claimantId?: string;
    };

const FormModalViewComponent: React.FunctionComponent<Props> = ({
    classes,
    formView,
    onSubmit,
    closeModal,
    openViewModal,
    formStyle,
    isButtonsHidden,
    modal,
    claim,
    claimantRequirements,
    lifeAssuredRequirements,
    previewScreen,
    showPreviewButton,
    claimantId,
}: Props) => {
    const FormView = formView;

    let documents: any[] = [];

    const requirements = modal?.form?.communicationsClaimant
        ? claimantRequirements.find((item: any) => item.id === modal.form.communicationsClaimant)
        : claimantRequirements[0];

    const claimantDocs =
        requirements?.documents?.filter((d: any) => d.status === 'Requested') || [];

    documents = [...claimantDocs];

    return (
        <Grid container justify="center">
            <Paper
                className={`${formStyle || ''} ${classes.paper}`}
                style={{
                    width: '90%',
                    maxHeight: 650,
                    overflowY: 'auto',
                }}
            >
                <FormView onSubmitSuccess={() => closeModal()} />

                {!isButtonsHidden && (
                    <Grid container justify="flex-end">
                        <Button
                            id="Cancel"
                            variant="contained"
                            className={classes.button}
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            id="Save"
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            onClick={submit}
                        >
                            Save
                        </Button>
                        {claim.claimType === ClaimType.Death && showPreviewButton && (
                            <Button
                                id="Preview"
                                variant="contained"
                                color="secondary"
                                className={classes.button}
                                onClick={() => preview(claimantId!)}
                                disabled={documents?.length === 0}
                            >
                                Preview E-mail
                            </Button>
                        )}
                    </Grid>
                )}
            </Paper>

            <CommunicationsPreviewEmail
                modalBody={previewScreen}
                sendCommunication={(payload: any) => Promise.resolve()}
            />
        </Grid>
    );

    async function submit() {
        if (onSubmit) {
            const result = await onSubmit();
            if (!result) return;
        }
        closeModal();
    }

    function preview(claimantId: string) {
        let documents: any[] = [];

        const requirements = claimantId
            ? claimantRequirements.find((item: any) => item.claimantId === claimantId)
            : claimantRequirements[0];

        const claimantDocs =
            requirements?.documents?.filter((d: any) => d.status === 'Requested') || [];

        documents = [...claimantDocs];

        const lifeAssuredDocs =
            lifeAssuredRequirements?.deceased
                ?.filter((doc: any) => doc.status === 'Requested')
                .map((doc: any) => ({
                    id: doc.id,
                    status: doc.status,
                    reason: doc.reason,
                    dateRequested: doc.dateRequested,
                    name: doc.name,
                })) || [];

        documents = [...documents, ...lifeAssuredDocs];

        const isLifeAssuredDocRequested = lifeAssuredDocs.length > 0;

        const claimants = getClaimantOptions(
            claim,
            false,
            isLifeAssuredDocRequested,
            documents,
            claim.claimType === ClaimType.Funeral
        );

        let claimant = claimants.find((c: any) => c.claimantId === claimantId);

        if (!claimant) {
            claimant = claimants[0];
        }

        if (claim.claimType !== ClaimType.Death) {
            openViewModal({
                isOpen: true,
                modalBody: () => ({
                    ...modal?.form,
                    claimantSurname: claimant?.surname,
                    communicationsTitle: claimant?.title,
                    documents,
                }),
            });
        }

        if (claim.claimType === ClaimType.Death) {
            openViewModal({
                isOpen: true,
                form: {
                    ...modal?.form,
                    claimantSurname: claimant?.surname,
                    communicationsTitle: claimant?.title,
                    communicationsPolicy: claimant?.communicationsPolicy?.split(',')?.[0]?.trim(),
                    claimantId: claimant?.claimantId,
                    idNumber: claimant?.idNumber,
                    documents,
                },
                modalBody: () => <MissingInformationContent />,
            });
        }
    }

    function onCancel() {
        closeModal();
        openViewModal({
            isOpen: false,
            form: {},
        });
    }
};

const mapStateToProps = (state: State) => ({
    modal: state.confirmationDialog,
    previewScreen: <MissingInformationContent />,
    claim: getClaim(state),
    claimantRequirements:
        (getDocumentsRequirementsFromClaim(getClaim(state)) as DeathRequirements).claimants || [],
    lifeAssuredRequirements:
        (getDocumentsRequirementsFromClaim(getClaim(state)) as DeathRequirements) || [],
    onSubmit: state.claimSession.formSubmission.onSubmit,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<State, any, AnyAction>) => ({
    closeModal: () => dispatch(closeModalAction()),
    openViewModal: (options: any) => dispatch(viewModalAction(options)),
    sendCommunication: (payload: any) => dispatch(communicationAction(payload)),
});

export const FormModalView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(FormModalViewComponent));
